
import React from 'react';
import { Product } from '../types';

interface ProductDetailViewProps {
  product: Product;
  onClose: () => void;
}

const ProductDetailView: React.FC<ProductDetailViewProps> = ({ product, onClose }) => {
  const [captionCopied, setCaptionCopied] = React.useState(false);
  const [imageCopied, setImageCopied] = React.useState(false);

  const handleCopyCaption = async () => {
    const caption = `${product.name}\n\n${product.description || ''}\n\nShop here: ${product.url}`;
    try {
      await navigator.clipboard.writeText(caption);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCopyImageLink = async () => {
    try {
      await navigator.clipboard.writeText(product.image);
      setImageCopied(true);
      setTimeout(() => setImageCopied(false), 2000);
    } catch (err) {
      console.error(err);
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator === 'undefined' || !navigator.share) return;
    const caption = `${product.name}\n\n${product.description || ''}\n\nShop here: ${product.url}`;
    try {
      await navigator.share({
        title: product.name,
        text: caption,
        url: product.url,
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white animate-fadeIn">
      
      {/* Sticky Top Header / Back Button - Stays fixed so only content scrolls */}
      <div className="sticky top-0 z-20 px-4 md:px-8 py-6 md:py-10 bg-white/80 backdrop-blur-md border-b border-slate-50">
        <div className="max-w-7xl mx-auto">
          <button 
            onClick={onClose}
            className="group flex items-center space-x-3 px-6 py-3 bg-white border border-slate-200 rounded-full text-[#5C3E43] font-black text-xs uppercase tracking-widest hover:bg-[#F1B1BA] hover:text-white hover:border-[#F1B1BA] transition-all shadow-sm"
          >
            <i className="fas fa-arrow-left transition-transform group-hover:-translate-x-1"></i>
            <span>Return to Products</span>
          </button>
        </div>
      </div>

      {/* Main Content Area - Scroll is limited to this section */}
      <div className="flex-1 overflow-y-auto px-4 pb-20 scroll-smooth custom-scrollbar">
        <div className="max-w-7xl mx-auto pt-8">
          
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 bg-white">
            
            {/* Image Section */}
            <div className="w-full lg:w-1/2 bg-white rounded-[3rem] border border-slate-100 flex items-center justify-center p-8 md:p-16 relative overflow-hidden h-fit">
              <div className="relative w-full aspect-square flex items-center justify-center bg-white">
                 <div className="absolute inset-0 bg-white rounded-full blur-3xl opacity-30 scale-125"></div>
                 <img 
                  src={product.image} 
                  alt={product.name} 
                  className="max-w-full max-h-full object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.1)] transition-transform duration-700 hover:scale-105"
                />
              </div>
            </div>

            {/* Details Section */}
            <div className="w-full lg:w-1/2 flex flex-col py-4 bg-white">
              <div className="mb-8">
                <div className="flex items-center space-x-4 mb-6">
                  <span className="px-4 py-1.5 bg-[#F1B1BA]/10 text-[#F1B1BA] text-[11px] font-black uppercase tracking-widest rounded-full border border-[#F1B1BA]/20">
                    {product.category}
                  </span>
                  <div className="flex items-center space-x-1 text-amber-500 text-[10px] font-black uppercase tracking-widest">
                    <i className="fas fa-certificate"></i>
                    <span>Official Expert Choice</span>
                  </div>
                </div>

                <h1 className="text-4xl md:text-6xl font-black text-[#5C3E43] uppercase tracking-tighter leading-none mb-6">
                  {product.name}
                </h1>

                <div className="flex items-center space-x-6">
                  <span className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">
                    {product.reviewCount} Expert Reviews
                  </span>
                </div>
              </div>

              <div className="mb-12">
                <div className="mb-8">
                  <h3 className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-4 flex items-center space-x-2">
                    <div className="w-8 h-px bg-slate-200"></div>
                    <span>Expert Product Insight</span>
                  </h3>
                  <p className="text-slate-600 text-lg leading-relaxed font-medium">
                    {product.description}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                  <div className="p-8 bg-white rounded-[2.5rem] border border-slate-200 flex flex-col justify-center shadow-sm">
                    <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Availability</span>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.4)]"></div>
                      <span className="text-xs font-black text-[#5C3E43] uppercase tracking-widest">Available for Dispatch</span>
                    </div>
                  </div>
                </div>

                 <div className="space-y-6">
                  <a 
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      const targetAsin = product.asin || product.id;
                      if (targetAsin) {
                        fetch('/api/track-click', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ asin: targetAsin })
                        }).catch((err) => console.error('Error tracking click:', err));
                      }
                    }}
                    className="flex w-full py-6 bg-[#F1B1BA] text-[#5C3E43] rounded-2xl font-black shadow-2xl shadow-pink-900/15 hover:scale-[1.01] active:scale-[0.99] transition-all items-center justify-center uppercase tracking-[0.25em] text-xs text-center animate-fadeIn"
                  >
                    Secure Purchase via {product.company || (
                      product.url.includes('amazon') || product.url.includes('amzlink') || product.url.includes('amzn.to') ? 'Amazon' :
                      product.url.includes('shopmy') ? 'ShopMy' :
                      product.url.includes('scampanddude') ? 'Scamp & Dude' :
                      product.url.includes('bonmarche') ? 'Bonmarché' :
                      product.url.includes('thecommense') ? 'Commense' :
                      product.url.includes('marksandspencer') ? 'M&S' : 'Official Partner'
                    )}
                  </a>

                  {/* Share actions */}
                  <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={handleCopyCaption}
                      className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-[#5C3E43] font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border-none"
                    >
                      <i className={captionCopied ? "fas fa-check text-green-500 animate-bounce" : "fas fa-copy"}></i>
                      <span>{captionCopied ? 'Caption Copied!' : 'Copy Caption'}</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleCopyImageLink}
                      className="flex-1 py-4 bg-slate-100 hover:bg-slate-200 text-[#5C3E43] font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border-none"
                    >
                      <i className={imageCopied ? "fas fa-check text-green-500" : "fas fa-image"}></i>
                      <span>{imageCopied ? 'Image Copied!' : 'Copy Image Link'}</span>
                    </button>

                    {typeof navigator !== 'undefined' && navigator.share && (
                      <button
                        type="button"
                        onClick={handleNativeShare}
                        className="py-4 px-6 bg-[#F1B1BA]/20 hover:bg-[#F1B1BA]/30 text-[#5C3E43] font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border-none"
                      >
                        <i className="fas fa-arrow-up-from-bracket"></i>
                        <span>Share...</span>
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-center space-x-6 opacity-30 py-4">
                    <i className="fab fa-cc-visa text-2xl"></i>
                    <i className="fab fa-cc-mastercard text-2xl"></i>
                    <i className="fab fa-cc-apple-pay text-2xl"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #F1B1BA;
        }
      `}</style>
    </div>
  );
};

export default ProductDetailView;
